import { UserService } from 'src/app/core/service/user.service';
import { Injectable, OnDestroy } from '@angular/core';
import { Observable, Subject, Subscription, forkJoin } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { TeamRole, Team } from 'src/app/team/model/team';
import { TeamRoleList, TeamRoleEdit } from 'src/app/team/model/team-role';
import { Cacheable, CacheBuster } from 'ts-cacheable';
import { TranslateService } from '@ngx-translate/core';
import { MessengerService } from './messenger.service';

const teamRolesCacheBuster = new Subject<void>();

@Injectable({
  providedIn: 'root'
})
export class TeamService implements OnDestroy
{
  /**
   * Localized notifications.
   */
  public notifications: Array<{ text: string; value: string }> = [
    { text: 'SMS', value: 'SMS' },
    { text: 'Email', value: 'EMAIL' },
    { text: 'No notification', value: 'NO_NOTIFICATION' }
  ];

  private messagingLanguageChangedSubscription: Subscription;

  constructor(
    private http: HttpClient,
    private translate: TranslateService,
    private messengerService: MessengerService,
    private userService: UserService)
  {
    this.loadLocalizations();
    this.messagingLanguageChangedSubscription = messengerService.receiveMessage().subscribe({
      next: (message) =>
      {
        if (message && message.type && message.type === 'languageChanged')
        {
          this.loadLocalizations();
        }
      }
    });
  }

  /**
   * Get team roles from REST service.
   *
   * @param id Member ID
   */
  @Cacheable(
    {
      cacheBusterObserver: teamRolesCacheBuster
    })
  public getCachedTeamRoles(): Observable<TeamRole[]>
  {
    return this.http.get<any>(`/team/roles`);
  }

  /**
   * Delete a Team Role.
   *
   * @param id Team Role id
   */
  @CacheBuster(
    {
      cacheBusterNotifier: teamRolesCacheBuster
    })
  public deleteTeamRole(id: number): Observable<TeamRoleEdit>
  {
    return this.http.delete<TeamRoleEdit>(`/team/role/${id}`);
  }

  /**
   * Save a Team Role.
   *
   * @param data Team Role to persit
   * @param isNew Indicate if this is a new or update
   */
  @CacheBuster(
    {
      cacheBusterNotifier: teamRolesCacheBuster
    })
  public saveTeamRole(data: any, isNew: boolean, teamRoleId?: number): Observable<any>
  {
    if (isNew)
    {
      return this.http.post('/team/role', data);
    }
    else
    {
      return this.http.put(`/team/role/${teamRoleId}`, data);
    }
  }

  public ngOnDestroy(): void
  {
    if (this.messagingLanguageChangedSubscription !== undefined) { this.messagingLanguageChangedSubscription.unsubscribe(); }
  }

  /**
   * Get team roles from REST service.
   *
   * @param id Member ID
   */
  public getTeamRoles(): Observable<TeamRole[]>
  {
    return this.http.get<any>(`/team/roles`);
  }

  /**
   * Save a Care team .
   *
   * @param careTeam CareTeam to save.
   */
  public createCareTeam(team: Team): Observable<any>
  {
    return this.http.post<any>('/team/careteam', team);
  }

  /**
   * Delete a Care team user.
   *
   * @param careTeam CareTeam to save.
   */
  public deleteCareTeam(teamId: number): Observable<any>
  {
    return this.http.delete<any>(`/team/careteam/${teamId}`);
  }

  /**
   * Get the care team for a member.
   *
   * @param id Member ID
   */
  public getCareTeamForMember(memberId: number): Observable<any>
  {
    return this.http.get<any>(`/team/member/${memberId}`);
  }

  public getAllCareTeamForMemberMultipleSources(memberId: number): Observable<any[]>
  {
    const careTeamUsers = this.getCareTeamForMember(memberId);
    const careTeamNoneUsers = this.userService.getNonUsersByMember(memberId, null);
    return forkJoin([careTeamUsers, careTeamNoneUsers]);
  }

  /**
   * Get the care team by id.
   *
   * @param id Care Team ID
   */
  public getCareTeam(id: number): Observable<any>
  {
    return this.http.get<any>(`/team/careteam/${id}`);
  }

  /**
   * Check if Care Team member exists for the direct address
   *
   * @param name Program Category Name
   */
  public existsProgramCategoryName(familyName: string, givenName: string, directAddress: string,
    caseId: number, memberId: number): Observable<any>
  {
    const baseUrl = `/team/exists?familyName=${familyName}&givenName=${givenName}&directAddress=${directAddress}`;
    if (caseId !== undefined && caseId > 0)
    {
      return this.http.get(`${baseUrl}&caseId=${caseId}`);
    }
    else
    {
      return this.http.get(`${baseUrl}&memberId=${memberId}`);
    }
  }

  public getTeamRoleDisplayText(teamRoles: any, value: string): string
  {
    const roleRole = teamRoles.find(x => x.id === parseInt(value, 10));
    if (roleRole !== undefined)
    {
      return roleRole.name;
    }
    else
    {
      return value;
    }
  }

  /**
   * Convert a list of team roles Views to views texts.
   *
   * @param views List of team roles.
   */
  public convertTeamRolesToDisplayTexts(teamRoles: any, views: string): string
  {
    const res = views.split(',');
    const viewsText = [];
    res.forEach(view =>
    {
      const viewText = this.getTeamRoleDisplayText(teamRoles, view);
      if (viewText !== '')
      {
        viewsText.push(viewText);
      }
    });
    return viewsText.join(', ');
  }

  /**
   * Get Team Roles Types.
   */
  public getTeamRolesList(): Observable<TeamRoleList[]>
  {
    return this.http.get<TeamRoleList[]>('/team/roles/list');
  }

  /**
   * Check if Team Role is in use
   *
   * @param id Team Role Id
   */
  public existsTeamRole(id: number): Observable<any>
  {
    return this.http.get(`/team/role/inuse/${id}`);
  }

  /**
   * Check if Team Role Name exists to avoid name duplicates
   *
   * @param name Team Role Name
   */
  public existsTeamRoleName(name: string): Observable<any>
  {
    return this.http.get(`/team/role/exists?name=${name}`);
  }

  /**
   * Get a Team Role by id.
   *
   * @param id Team Role id
   */
  public getTeamRoleById(id: number): Observable<TeamRoleEdit>
  {
    return this.http.get<TeamRoleEdit>(`/team/role/${id}`);
  }

  /**
   * Load localizations
   */
  private loadLocalizations(): void
  {
    this.translate.get(['SMS', 'EMAIL', 'NO_NOTIFICATION']).subscribe({
      next: (res) =>
      {
        let index = 0;
        for (const key in res)
        {
          if ({}.hasOwnProperty.call(res, key))
          {
            this.notifications[index].text = res[key];
            index++;
          }
        }
      }
    });
  }
}
